/**
 * A Wireline & Slickline ticket's lines (GET /wireline-slickline/tickets/:id → lines).
 *
 * Unpriced lines are FLAGGED on the row with an "Unpriced" chip and their
 * money reads `—`, never 0 — the office approve refuses while any remain
 * (`ticket_unpriced`). The totals are the API's: on a draft the priced lines'
 * sum (`draftSubtotal`); while any line is unpriced the TOTAL reads `—` with
 * the flag, because no total exists until every line has a rate — and a sum
 * over no priced line is not shown as $0. Nothing here adds money up. Adding, editing and deleting a line is offered only
 * while the ticket is editable (`editable`): a draft, or a signed ticket the
 * office has chosen to unlock (the screen asked first; the edit returns it to
 * draft). A drafted ('auto') line keeps its unit (`unit_fixed`) and its depth
 * or shot basis (`basis_fixed`). A WL ticket carries NO TAX: the money is the
 * API's subtotal.
 *
 * HOW A LINE IS PRICED
 *   from the rate card     `rate_id` (a third-party rate also takes the vendor's cost)
 *   the office's own rate  `kind` + `unit` + `rate`
 *   a third-party cost     `kind: third_party` + `unit: third_party` + `third_party_cost` (+ `markup_pct`) —
 *                          the API prices it; it never takes a typed `rate` (`third_party_rate`)
 *   no price yet           `kind` + `unit`; the line is unpriced
 *
 * A per-depth line names its depth basis and a per-shot line its shot basis
 * (`basis_not_for_unit` otherwise); the depth and distance units are the
 * tenant's, stamped by the API. The unit picker offers only the units the API
 * accepts for the kind (WL_KIND_UNITS); the API still decides (`unit_not_for_kind`).
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddWlTicketLine, useRemoveWlTicketLine, useUpdateWlTicketLine, type WlTicketEditResult } from '../../../hooks/useWlTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { wlTicketRefusal, wlUnitsForKind } from '../../../lib/wireline-slickline-tickets';
import { fmtWlNumber, wlToInput } from '../../../lib/wireline-slickline';
import { wlDay } from '../../../lib/wireline-slickline-jobs';
import type { WlRate, WlTicket, WlTicketCatalog, WlTicketLine, WlTicketLineCreate, WlTicketLinePatch } from '../../../types/wireline-slickline-tickets';
import { WL_DATE_INPUT, WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlEmpty, WlField, WlFormError, WlPanel } from '../WlBits';
import { PfInlineConfirm as WlInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { wlRateOption, wlRateText, ratesForTicket, WlUnpricedFlag } from './TicketBits';

type PriceMode = 'keep' | 'card' | 'office' | 'vendor' | 'clear';

const isNum = (v: string) => v.trim() !== '' && Number.isFinite(Number(v));

/** The line form: add (no `line`) or edit. Sends only what changed on an edit. */
export function WlTicketLineForm({
  ticket,
  line,
  rates,
  catalog,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  ticket: WlTicket;
  line?: WlTicketLine;
  rates: WlRate[];
  catalog: WlTicketCatalog | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: WlTicketLineCreate | WlTicketLinePatch) => void;
}) {
  const adding = !line;
  const thirdPartyLine = !!line && line.kind === 'third_party';
  const [description, setDescription] = useState(line?.description ?? '');
  const [quantity, setQuantity] = useState(wlToInput(line?.quantity));
  const [kind, setKind] = useState(line?.kind ?? '');
  const [unit, setUnit] = useState(line?.unit ?? '');
  const [depthBasis, setDepthBasis] = useState(line?.depthBasis ?? '');
  const [shotBasis, setShotBasis] = useState(line?.shotBasis ?? '');
  const [mode, setMode] = useState<PriceMode>(adding ? 'card' : thirdPartyLine ? 'vendor' : 'keep');
  const [rateId, setRateId] = useState('');
  // Leaving "A third-party cost" while adding clears the kind and unit it set:
  // the Kind list hides third_party outside that mode, so a kept value would
  // send a third-party line the screen no longer shows.
  const leaveVendor = (next: PriceMode) => {
    if (adding && mode === 'vendor' && next !== 'vendor') { setKind(''); setUnit(''); }
    setMode(next);
  };
  const [rate, setRate] = useState('');
  const [cost, setCost] = useState(wlToInput(line?.thirdPartyCost));
  const [markup, setMarkup] = useState(wlToInput(line?.markupPct));
  const [workDate, setWorkDate] = useState(ticket.scope === 'day' ? ticket.workDate ?? '' : '');
  const [problem, setProblem] = useState<string | null>(null);
  const unitFixed = !!line && line.source !== 'office';
  const effUnit = unit || null;
  const cardRates = ratesForTicket(rates, ticket, { unit: adding ? null : effUnit, on: ticket.workDate }).filter(
    // An edit re-prices a line of its own kind; a third-party rate prices a third-party line only (`rate_kind_mismatch`).
    (r) => adding || r.kind !== 'third_party',
  );
  const picked = cardRates.find((r) => r.id === rateId) ?? null;
  const pickedThirdParty = picked?.kind === 'third_party';
  const kinds = (catalog?.priceKinds ?? []).filter((k) => (mode === 'vendor' ? k.value === 'third_party' : k.value !== 'third_party'));
  // On an edit the line's own kind narrows the units it may move to.
  const allowedUnits = wlUnitsForKind(adding ? (mode === 'vendor' ? 'third_party' : kind) : line!.kind);
  const units = (catalog?.priceUnits ?? []).filter((u) => !allowedUnits || allowedUnits.includes(u.value));
  const pickKind = (next: string) => {
    setKind(next);
    const allowed = wlUnitsForKind(next);
    if (allowed && allowed.length === 1) setUnit(allowed[0]);
    else if (allowed && !allowed.includes(unit)) setUnit('');
  };
  const basisFixed = unitFixed;

  const submit = () => {
    setProblem(null);
    if (!description.trim()) return setProblem('Enter what the line is for.');
    if (!isNum(quantity)) return setProblem('Enter the quantity as a number.');
    if (mode === 'card' && !rateId) return setProblem('Pick a rate from the rate card, or price the line another way.');
    if (mode === 'office' && !isNum(rate)) return setProblem('Enter the rate as a number.');
    if ((mode === 'vendor' || pickedThirdParty) && !isNum(cost)) return setProblem('Enter the vendor’s cost as a number.');
    if ((mode === 'vendor' || pickedThirdParty) && markup.trim() && !isNum(markup)) return setProblem('Enter the markup as a percentage, or leave it blank.');
    if (adding) {
      if (mode !== 'card' && mode !== 'vendor' && (!kind || !unit)) return setProblem('Pick what kind of charge it is and its unit.');
      if (mode === 'vendor' && !unit) return setProblem('Pick the unit the vendor’s cost is counted in.');
      if (mode !== 'card' && unit === 'per_depth' && !depthBasis) return setProblem('Pick what the depth is counted on: max depth, footage run or the interval.');
      if (mode !== 'card' && unit === 'per_shot' && !shotBasis) return setProblem('Pick what the shots are counted on: fired or loaded.');
      const body: WlTicketLineCreate = { description: description.trim(), quantity: quantity.trim() };
      if (mode === 'card') {
        body.rate_id = rateId;
        if (pickedThirdParty) {
          body.third_party_cost = cost.trim();
          if (markup.trim()) body.markup_pct = markup.trim();
        }
      } else {
        body.kind = mode === 'vendor' ? 'third_party' : kind;
        body.unit = unit;
        if (unit === 'per_depth') body.depth_basis = depthBasis;
        if (unit === 'per_shot') body.shot_basis = shotBasis;
        if (mode === 'office') body.rate = rate.trim();
        if (mode === 'vendor') {
          body.third_party_cost = cost.trim();
          if (markup.trim()) body.markup_pct = markup.trim();
        }
      }
      if (ticket.scope === 'job' && workDate) body.work_date = workDate;
      return onSubmit(body);
    }
    const body: WlTicketLinePatch = {};
    if (description.trim() !== line!.description) body.description = description.trim();
    if (Number(quantity) !== line!.quantity) body.quantity = quantity.trim();
    if (!unitFixed && unit && unit !== line!.unit) body.unit = unit;
    if (!basisFixed && unit === 'per_depth' && depthBasis && depthBasis !== line!.depthBasis) body.depth_basis = depthBasis;
    if (!basisFixed && unit === 'per_shot' && shotBasis && shotBasis !== line!.shotBasis) body.shot_basis = shotBasis;
    if (thirdPartyLine) {
      if (Number(cost) !== line!.thirdPartyCost) body.third_party_cost = cost.trim();
      const m = markup.trim() === '' ? 0 : Number(markup);
      if (m !== (line!.markupPct ?? 0)) body.markup_pct = markup.trim() === '' ? null : markup.trim();
    }
    if (mode === 'card') body.rate_id = rateId;
    if (mode === 'office') body.rate = rate.trim();
    if (mode === 'clear') body.rate = null;
    if (!Object.keys(body).length) return setProblem('Nothing has changed.');
    return onSubmit(body);
  };

  const vendorFields = (
    <>
      <WlField label={`Vendor’s cost (${ticket.currency ?? '—'}) *`} hint="Per unit, before any markup.">
        <input className={WL_INPUT} inputMode="decimal" aria-label="Vendor’s cost" value={cost} onChange={(e) => setCost(e.target.value)} />
      </WlField>
      <WlField label="Markup %" hint={pickedThirdParty ? 'Blank = the rate’s own pricing.' : 'Blank = billed at cost.'}>
        <input className={WL_INPUT} inputMode="decimal" aria-label="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} />
      </WlField>
    </>
  );

  return (
    <div data-testid={adding ? 'wl-ticket-line-add-form' : 'wl-ticket-line-edit-form'} className="space-y-3 border-t border-fieldiq-border bg-fieldiq-dark/40 px-4 py-4">
      <WlFormError message={problem ?? error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <WlField label="Description *">
            <input className={WL_INPUT} value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
          </WlField>
        </div>
        <WlField label="Quantity *">
          <input className={WL_INPUT} inputMode="decimal" aria-label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </WlField>
        {adding && ticket.scope === 'job' && (
          <WlField label="Work day" hint="Optional.">
            <input className={WL_DATE_INPUT} type="date" aria-label="Work day" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
          </WlField>
        )}
      </div>
      <fieldset className="space-y-2">
        <legend className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Price</legend>
        {thirdPartyLine ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{vendorFields}</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 text-sm text-fieldiq-text">
              {!adding && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="wl-price" checked={mode === 'keep'} onChange={() => leaveVendor('keep')} /> Keep the price
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="radio" name="wl-price" checked={mode === 'card'} onChange={() => leaveVendor('card')} /> From the rate card
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="wl-price" checked={mode === 'office'} onChange={() => leaveVendor('office')} /> The office’s own rate
              </label>
              {adding && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="wl-price" checked={mode === 'vendor'} onChange={() => { setMode('vendor'); setKind('third_party'); setUnit('third_party'); }} /> A third-party cost
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="radio" name="wl-price" checked={mode === 'clear'} onChange={() => leaveVendor('clear')} /> {adding ? 'No price yet' : 'Clear the price'}
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {mode === 'card' && (
                <div className="sm:col-span-2">
                  <WlField label="Rate" hint={adding ? 'The rate sets the kind and unit.' : 'Rates counted in this line’s unit.'}>
                    <select className={WL_INPUT} aria-label="Rate" value={rateId} onChange={(e) => setRateId(e.target.value)}>
                      <option value="">{cardRates.length ? 'Pick a rate' : 'No rate on the card fits this line'}</option>
                      {cardRates.map((r) => (
                        <option key={r.id} value={r.id}>
                          {wlRateOption(r)}
                        </option>
                      ))}
                    </select>
                  </WlField>
                </div>
              )}
              {mode === 'card' && pickedThirdParty && vendorFields}
              {mode === 'office' && (
                <WlField label={`Rate (${ticket.currency ?? '—'})`}>
                  <input className={WL_INPUT} inputMode="decimal" aria-label="Office rate" value={rate} onChange={(e) => setRate(e.target.value)} />
                </WlField>
              )}
              {mode === 'vendor' && vendorFields}
              {adding && mode !== 'card' && mode !== 'vendor' && (
                <WlField label="Kind *">
                  <select className={WL_INPUT} aria-label="Kind" value={kind} onChange={(e) => pickKind(e.target.value)}>
                    <option value="">Pick one</option>
                    {kinds.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </WlField>
              )}
              {(adding ? mode !== 'card' : !unitFixed) && (
                <WlField label={adding ? 'Unit *' : 'Unit'}>
                  <select className={WL_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
                    <option value="">Pick one</option>
                    {units.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </WlField>
              )}
              {(adding ? mode !== 'card' : !basisFixed) && unit === 'per_depth' && (
                <WlField label="Depth basis *">
                  <select className={WL_INPUT} aria-label="Depth basis" value={depthBasis} onChange={(e) => setDepthBasis(e.target.value)}>
                    <option value="">Pick one</option>
                    {(catalog?.depthBases ?? []).map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </WlField>
              )}
              {(adding ? mode !== 'card' : !basisFixed) && unit === 'per_shot' && (
                <WlField label="Shot basis *">
                  <select className={WL_INPUT} aria-label="Shot basis" value={shotBasis} onChange={(e) => setShotBasis(e.target.value)}>
                    <option value="">Pick one</option>
                    {(catalog?.shotBases ?? []).map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </WlField>
              )}
            </div>
          </>
        )}
        {unitFixed && <p className="text-xs text-fieldiq-muted">A line drafted from the day sheets keeps the unit its runs, hours or days were counted in, and its depth or shot basis.</p>}
      </fieldset>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={WL_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : adding ? 'Add line' : 'Save line'}
        </button>
      </div>
    </div>
  );
}

/** A line's quantity unit: m / ft on a depth line (with its basis), km / mi on a distance line, the unit's label otherwise. */
const qtyUnit = (l: WlTicketLine) => {
  if (l.unit === 'per_depth') return [l.depthUnit, l.depthBasisLabel].filter(Boolean).join(' · ') || l.unitLabel || l.unit;
  if (l.unit === 'per_shot') return [l.unitLabel ?? 'Per shot', l.shotBasisLabel].filter(Boolean).join(' · ');
  if (l.unit === 'per_distance') return l.distanceUnit ?? l.unitLabel ?? l.unit;
  return l.unitLabel ?? l.unit;
};

export function WlTicketLines({
  ticket,
  editable,
  canWrite,
  rates,
  catalog,
  highlight,
  onEdited,
}: {
  ticket: WlTicket;
  /** Draft, or a signed ticket the office unlocked. */
  editable: boolean;
  canWrite: boolean;
  rates: WlRate[];
  catalog: WlTicketCatalog | null;
  /** Line ids an `ticket_unpriced` refusal named. */
  highlight: string[];
  onEdited: (r: WlTicketEditResult) => void;
}) {
  const add = useAddWlTicketLine();
  const update = useUpdateWlTicketLine();
  const remove = useRemoveWlTicketLine();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lines = ticket.lines ?? [];
  const cur = ticket.currency;
  const write = editable && canWrite;
  const draft = ticket.status === 'draft';
  const unpriced = ticket.unpricedCount ?? lines.filter((l) => l.unpriced).length;
  // A count of lines, not money: whether any line has a price to show a sum of.
  const pricedLines = lines.filter((l) => !l.unpriced).length;

  const done = (msg: string) => (r: WlTicketEditResult) => {
    toast.success(msg);
    setAdding(false);
    setEditingId(null);
    setDeletingId(null);
    onEdited(r);
  };

  return (
    <WlPanel
      eyebrow="Lines"
      title={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
      testId="wl-ticket-lines"
      actions={
        write && !adding ? (
          <button type="button" className={WL_SECONDARY} onClick={() => { setError(null); setEditingId(null); setAdding(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Add line
          </button>
        ) : undefined
      }
    >
      {(ticket.unpricedCount ?? 0) > 0 && (
        <p data-testid="wl-ticket-unpriced-count" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-2 text-sm text-fieldiq-text">
          {ticket.unpricedCount === 1 ? 'One line has no rate.' : `${ticket.unpricedCount} lines have no rate.`} Price {ticket.unpricedCount === 1 ? 'it' : 'them'} or remove{' '}
          {ticket.unpricedCount === 1 ? 'it' : 'them'} before the office approves the ticket.
        </p>
      )}
      {adding && (
        <WlTicketLineForm
          ticket={ticket}
          rates={rates}
          catalog={catalog}
          pending={add.isPending}
          error={error}
          onCancel={() => setAdding(false)}
          onSubmit={(body) => {
            setError(null);
            add.mutate({ id: ticket.id, body: body as WlTicketLineCreate }, { onSuccess: done('Line added'), onError: (e) => setError(wlTicketRefusal(e, 'The line could not be added.')) });
          }}
        />
      )}
      {!lines.length ? (
        <WlEmpty>No lines. Refresh the ticket from the day sheets, or add a line.</WlEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-fieldiq-border text-left text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <th className="px-4 py-2">Line</th>
                <th className="px-4 py-2">Day</th>
                <th className="px-4 py-2 text-right">Quantity</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2 text-right">Amount</th>
                {write && <th className="px-4 py-2 text-right"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <React.Fragment key={l.id}>
                  <tr
                    data-testid="wl-ticket-line"
                    data-unpriced={l.unpriced ? 'true' : undefined}
                    className={`border-b border-fieldiq-border align-top ${l.unpriced ? 'bg-fi-signal-red/10' : ''} ${highlight.includes(l.id) ? 'outline outline-1 outline-fi-signal-red' : ''}`}
                  >
                    <td className="px-4 py-2">
                      <p className="font-semibold text-white">{l.description || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {l.kindLabel || l.kind || '—'}
                        {l.runKindLabel ? ` · ${l.runKindLabel}` : ''}
                        {l.crewPositionLabel ? ` · ${l.crewPositionLabel}` : ''} ·{' '}
                        <span data-testid="wl-ticket-line-source">{l.source === 'office' ? 'Added by the office' : 'From the day sheets'}</span>
                        {l.rateCode ? ` · ${l.rateCode}` : l.rateSource === 'office' ? ' · Office rate' : ''}
                      </p>
                      {l.thirdPartyCost !== null && (
                        <p className="mt-0.5 text-xs text-fieldiq-muted">
                          Vendor’s cost {fmtCurrency(l.thirdPartyCost, cur)}
                          {l.markupPct ? ` + ${l.markupPct}%` : ' at cost'}
                        </p>
                      )}
                      {l.unpriced && (
                        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-fieldiq-text">
                          <span data-testid="wl-ticket-line-unpriced" className="rounded-full bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase tracking-[0.08em] text-white">
                            Unpriced
                          </span>
                          {l.unpricedReason && <span>{l.unpricedReason}</span>}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2">{wlDay(l.workDate)}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {fmtWlNumber(l.quantity)} {qtyUnit(l)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{wlRateText(l.rate, cur, l.unit, l.depthUnit, l.distanceUnit)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtCurrency(l.amount, cur)}</td>
                    {write && (
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button type="button" aria-label={`Edit ${l.description}`} className={WL_SECONDARY} onClick={() => { setError(null); setAdding(false); setEditingId(l.id); }}>
                            <Pencil className="size-4" aria-hidden="true" />
                          </button>
                          <button type="button" aria-label={`Delete ${l.description}`} className={WL_SECONDARY} onClick={() => { setError(null); setDeletingId(l.id); }}>
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {write && deletingId === l.id && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3">
                        <WlInlineConfirm
                          testId="wl-ticket-line-delete-confirm"
                          message={`Remove “${l.description}” from the ticket?${l.source === 'auto' ? ' A refresh from the day sheets will draw it again.' : ''}`}
                          confirmLabel="Remove line"
                          cancelLabel="Keep it"
                          pending={remove.isPending}
                          error={error}
                          onCancel={() => setDeletingId(null)}
                          onConfirm={() =>
                            remove.mutate({ id: ticket.id, lineId: l.id }, { onSuccess: done('Line removed'), onError: (e) => setError(wlTicketRefusal(e, 'The line could not be removed.')) })
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {write && editingId === l.id && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <WlTicketLineForm
                          ticket={ticket}
                          line={l}
                          rates={rates}
                          catalog={catalog}
                          pending={update.isPending}
                          error={error}
                          onCancel={() => setEditingId(null)}
                          onSubmit={(body) =>
                            update.mutate(
                              { id: ticket.id, lineId: l.id, body: body as WlTicketLinePatch },
                              { onSuccess: done('Line saved'), onError: (e) => setError(wlTicketRefusal(e, 'The line could not be saved.')) },
                            )
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl data-testid="wl-ticket-totals" className="grid gap-1 border-t border-fieldiq-border px-4 py-3 text-sm sm:ml-auto sm:w-80">
        {draft ? (
          <>
            <TotalRow
              label={unpriced > 0 ? 'Subtotal (priced lines only)' : 'Subtotal (priced lines)'}
              value={pricedLines > 0 ? fmtCurrency(ticket.draftSubtotal, cur) : '—'}
              testId="wl-ticket-draft-subtotal"
            />
            {unpriced > 0 && <TotalRow label="Ticket subtotal" value="—" flag={unpriced} testId="wl-ticket-draft-total" strong />}
          </>
        ) : (
          <TotalRow label="Subtotal" value={fmtCurrency(ticket.subtotal, cur)} testId="wl-ticket-subtotal" strong />
        )}
        {draft && (
          <p className="text-xs text-fieldiq-muted">
            {unpriced > 0 ? 'No ticket subtotal until every line has a rate. ' : ''}The subtotal is fixed when the office approves the ticket. A field ticket
            carries no tax.
          </p>
        )}
      </dl>
    </WlPanel>
  );
}

function TotalRow({ label, value, strong, flag, testId }: { label: string; value: string; strong?: boolean; flag?: number; testId?: string }) {
  return (
    <div className="flex justify-between gap-4" data-testid={testId}>
      <dt className="text-fieldiq-muted">{label}</dt>
      <dd className={`font-mono ${strong ? 'font-extrabold text-white' : 'text-fieldiq-text'}`}>
        {value}
        {flag ? <WlUnpricedFlag count={flag} testId="wl-ticket-total-unpriced" /> : null}
      </dd>
    </div>
  );
}

